import { EmptyState, Pane } from '@/components/kit';

/**
 * WHAT A SETTINGS SCREEN SAYS WHEN THE RECORD NEVER ARRIVED.
 *
 * Facility and Branding are both one record edited in place, which makes them
 * the exact shape BRIEF §4.10 forbids getting wrong: a read that is refused or
 * fails leaves `isLoading` false and the value null, and a form gated only on
 * "still loading" then renders every field as an empty editable box. A manager
 * reading that sees "PHONE — Not set" and believes this clinic has no phone
 * number; worse, the defaults the form falls back on ("English", "EGP —
 * Egyptian pound") are presented as saved settings nobody ever saved.
 *
 * So the record not resolving is its OWN state, designed rather than defaulted
 * (§4.13), and it is deliberately not a form:
 *
 *   • a refusal (403) says whose the record is, because there is nothing the
 *     reader can do about it and "try again" would only be refused again;
 *   • anything else says the read did not come back and offers a real retry;
 *   • both say nothing was changed, which is the question anyone looking at a
 *     settings screen that lost its record actually has;
 *   • the screen publishes NO Save while it is in this state — a primary with
 *     no draft behind it is §4.8's dead control, and that is what the round-2
 *     review caught on all three sub-screens.
 *
 * Reached by any account without `hospital_settings:read` — which is every
 * role but Owner and Clinic Manager, including the Doctor surface this role
 * is folded under.
 */

/** 403 is the answer to "whose is this?", anything else to "did it arrive?". */
function isRefusal(error: unknown): boolean {
  const status = (error as { response?: { status?: number } })?.response?.status;
  return status === 401 || status === 403;
}

export function SettingsUnreadable({
  what,
  error,
  onRetry,
}: {
  /** The record, named the way the screen names it: "facility", "branding". */
  what: string;
  error: unknown;
  onRetry: () => void;
}) {
  const refused = isRefusal(error);
  return (
    <EmptyState
      className="mt12"
      title={refused
        ? `This clinic’s ${what} is not yours to read`
        : `This clinic’s ${what} could not be read`}
      sub={refused
        ? `System Settings belongs to the clinic’s owner and its manager. This account is neither, so nothing about this clinic is shown here — and nothing has been changed.`
        : `The record did not come back, so there is nothing to edit yet. Nothing has been changed.`}
      action={refused ? undefined : { label: 'Try again', onClick: onRetry }}
    />
  );
}

/**
 * The pane's half of the same state. The pane is an extension of the selection
 * (Law 4) and there is no selection when there is no record — so it says which,
 * instead of drawing a preset that is not there under a heading that claims one.
 */
export function UnreadablePane({ what, error }: { what: string; error: unknown }) {
  return (
    <Pane>
      <div className="emptystate">
        <p className="t-sec">
          {isRefusal(error)
            ? `Nothing to preview — this clinic’s ${what} is not yours to read.`
            : `Nothing to preview — this clinic’s ${what} could not be read.`}
        </p>
      </div>
    </Pane>
  );
}
